'use client'

import TrackedLink from '@/components/utils/tracking/tracked-link'
import { cn } from '@/lib/utils'
import Image from 'next/image'
import { usePathname } from 'next/navigation'

/**
 * A reusable call-to-action component for promoting livery design
 * @param {Object} props - Component props
 * @param {Object} props.session - User's session data, if it exists
 * @param {string} props.className - Additional CSS classes
 */
export default function LiveryDesignCTA({ session, className = '' }) {
  const pathname = usePathname()

  return (
    <div
      className={cn(
        'relative mx-auto flex h-full rounded-lg font-inter',
        className,
      )}
    >
      <div className="relative flex flex-col py-10 tablet:py-12 desktop-xs:flex-row desktop-xs:py-12">
        <div className="desktop-s z-11 mx-auto flex w-[80%] flex-col pb-8 desktop-xs:mx-0 desktop-xs:my-auto desktop-xs:w-[50%] desktop-xs:px-16 desktop-xs:pb-0 desktop-xs:pr-10">
          <div className="mt-auto text-[24px] font-bold leading-[28px]">
            Design liveries for{' '}
            <span className="gf-gradient-text inline-flex items-center">
              every racer
            </span>
          </div>
          <div className="mt-[24px] text-[16px] font-normal text-neutral-blue-500">
            Create livery designs that will reach a vast and enthusiastic
            audience. By designing for this large group, your work gains the
            exposure it deserves, connecting you with sim racers from all
            corners of the globe. It's your opportunity to impact the sim racing
            world with your creative vision.
          </div>

          {!session && (
            <TrackedLink
              data-tracking-event={'Sign up button clicked'}
              trackingdata={{
                type: 'livery-design-cta',
              }}
              href={`/auth/login?redirectUrl=${pathname}&signup=true`}
              className="mx-auto mt-10 flex items-center rounded-sm bg-white p-4 align-middle text-black"
            >
              <span className="font-inter font-semibold">
                Create a free profile
              </span>
            </TrackedLink>
          )}
        </div>
        <div className="z-11 mx-auto flex w-[80%] desktop-xs:mx-0 desktop-xs:w-[50%] desktop-xs:pl-3 desktop-xs:pr-8">
          <div className="relative m-auto flex h-auto w-full flex-col">
            <div className="z-1 relative mx-px my-px h-[calc(100%-2px)] w-[calc(100%-2px)] rounded-[11px] bg-neutral-blue-800 pt-[55%] desktop-xs:pt-[56%]">
              <Image
                src="/img/frontend/livery-cta-livery.webp"
                alt="An image of the livery designer"
                fill
                className="w-[calc(100%)]! object-cover! left-0 top-0 h-full rounded-lg"
              />
            </div>
            <div className="absolute flex h-full w-full flex-col md:flex-row">
              <div className="bg-linear-to-b h-full w-full rounded-lg from-neutral-blue-700 from-80% to-neutral-blue-800" />
            </div>
          </div>
        </div>
      </div>

      <div className="z-9 absolute flex h-full w-full flex-col desktop-xs:flex-row">
        <div className="bg-linear-to-b desktop-xs:bg-linear-to-r h-[50%] w-full rounded-t-lg from-neutral-blue-700 to-neutral-blue-900 to-70% desktop-xs:h-full desktop-xs:rounded-l-lg" />
        <div className="bg-linear-to-b desktop-xs:bg-linear-to-r h-[50%] w-full rounded-b-lg from-neutral-blue-900 from-30% to-neutral-blue-700 desktop-xs:h-full desktop-xs:rounded-r-lg" />
      </div>
      <div className="absolute z-10 m-[2px] flex h-[calc(100%-4px)] w-[calc(100%-4px)] flex-col desktop-xs:flex-row">
        <div className="bg-linear-to-b desktop-xs:bg-linear-to-r h-[50%] w-full rounded-t-[10px] from-neutral-blue-800 to-neutral-blue-900 to-70% desktop-xs:h-full desktop-xs:rounded-l-[10px]" />
        <div className="bg-linear-to-b desktop-xs:bg-linear-to-r h-[50%] w-full rounded-b-[10px] from-neutral-blue-900 from-30% to-neutral-blue-800 desktop-xs:h-full desktop-xs:rounded-r-[10px]" />
      </div>
    </div>
  )
}
